import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import ListItemText from "@mui/material/ListItemText";

import useMutation from "../../hooks/useMutation";
import { useProjectInventory } from "../../contexts/projectInventory";
import useQueryParams from "../../hooks/useQueryParams";
import useNotification from "../../hooks/notification";
import { usePermissions } from "../../contexts/permissions";
import ImportAssetsButton from "./ImportAssetsButton";
import ImportProcurementSheetButton from "./ImportProcurementSheetButton";
import FindReplaceWizard from "./FindReplaceWizard";

import {
  DOWNLOAD_ASSETS_IMAGES_MUTATION,
  EXPORT_DONATIONS_REPORT_MUTATION,
  EXPORT_EXCEL_DATA_MUTATION,
} from "src/constants/graphql";

import LookupScanner from "src/components/barcode/LookupScanner";
import SelectProjectAttentionModal from "src/components/capture/SelectProjectAttentionModal";
import MoreIcon from "@mui/icons-material/MoreVert";
import NewIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Description as ExcelIcon,
  Publish as ImportAssetsIcon,
  FindReplace as FindReplaceIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Build as ToolsIcon,
} from "@mui/icons-material";
import MenuActions from "src/components/MenuActions";
import { useAssetsCollection } from "src/contexts/assetsCollection";
import useRoles from "../../hooks/user/useRoles";
import ProjectInCartQuantityModal from "./ProjectInCartQuantityModal";

const reportGenerationNotification =
  "We started generating report. You'll receive notice when it's ready";

const MoreLabel = () => (
  <>
    <MoreIcon fontSize="small" color="action" />
    <Box mr={0.5} />
    <Typography color="textSecondary" variant="body2">
      More
    </Typography>
  </>
);

// Hook: detect mobile viewport
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);

  return isMobile;
};

// Nested Menu Item — controlled via shared activeSubmenu / setActiveSubmenu
const NestedMenuItem = ({ label, icon, children, popupId, activeSubmenu, setActiveSubmenu }) => {
  const isMobile = useIsMobile();
  const [openToLeft, setOpenToLeft] = useState(false);
  const menuItemRef = useRef(null);
  const closeTimeout = useRef(null);
  const isOpen = activeSubmenu === popupId;

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  const calcDirection = useCallback(() => {
    if (!menuItemRef.current) return;
    const { right } = menuItemRef.current.getBoundingClientRect();
    setOpenToLeft(window.innerWidth - right < 250);
  }, []);

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    calcDirection();
    setActiveSubmenu(popupId);
  }, [calcDirection, popupId, setActiveSubmenu]);

  const scheduleClose = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setActiveSubmenu(prev => (prev === popupId ? null : prev));
    }, 200);
  }, [popupId, setActiveSubmenu]);

  const cancelClose = useCallback(() => clearTimeout(closeTimeout.current), []);

  return (
    <>
      <MenuItem
        ref={menuItemRef}
        onMouseEnter={!isMobile ? open : undefined}
        onMouseLeave={!isMobile ? scheduleClose : undefined}
        onClick={isMobile ? open : undefined}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          {icon && <Box mr={1}>{icon}</Box>}
          <ListItemText primary={label} />
        </Box>
        <ChevronRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        open={isOpen}
        anchorEl={menuItemRef.current}
        onClose={() => setActiveSubmenu(null)}
        style={{ pointerEvents: "none" }}
        MenuListProps={{
          style: { pointerEvents: "auto" },
          onMouseEnter: cancelClose,
          onMouseLeave: scheduleClose,
        }}
        anchorOrigin={{ vertical: "top", horizontal: openToLeft ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: openToLeft ? "right" : "left" }}
      >
        {children}
      </Menu>
    </>
  );
};

const TableBulkActions = ({
  selectedIds,
  totalCount,
  onCompleteBulk,
  moreActions,
  setDisabledScroll,
  refetch,
  fields,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();
  const { projectId, clientId } = useProjectInventory();
  const [exportDonationsReport] = useMutation(EXPORT_DONATIONS_REPORT_MUTATION);
  const [exportExcelData] = useMutation(EXPORT_EXCEL_DATA_MUTATION);
  const [downloadAssetsImages] = useMutation(DOWNLOAD_ASSETS_IMAGES_MUTATION);
  const isFindReplaceOpen = params.has("findReplace");
  const [isAttentionOpen, setIsAttentionOpen] = useState(false);

  const { notifySuccess, notifyError } = useNotification();
  const { can } = usePermissions();
  const canViewShoppingCarts = useRoles(["admin", "manager", "client"]);
  const isProjectInCartModalOpen = canViewShoppingCarts && params.has("shoppingCarts");

  const { bulkRemoveAssets, createAsset } = useAssetsCollection();

  const [lookupScanner, setLookupScanner] = useState(false);
  const toggleLookupScanner = () => setLookupScanner(v => !v);

  const requiresProject = () => {
    if (projectId) return false;
    setIsAttentionOpen(true);
    return true;
  };

  const openFindReplace = () => {
    if (requiresProject()) return;
    const newParams = new URLSearchParams(location.search);
    newParams.set("findReplace", "0");
    navigate({ search: newParams.toString() });
  };

  const openProjectInCartModal = () => {
    if (requiresProject()) return;
    const newParams = new URLSearchParams(location.search);
    newParams.set("shoppingCarts", "1");
    navigate({ search: newParams.toString() });
  };

  const closeProjectInCartModal = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete("shoppingCarts");
    navigate({ search: newParams.toString() });
  };

  const exportDonations = () => {
    exportDonationsReport({ variables: { projectId } });
    notifySuccess(reportGenerationNotification);
  };

  const exportExcel = () => {
    exportExcelData({ variables: { projectId } });
    notifySuccess(reportGenerationNotification);
  };

  const downloadImages = () => {
    downloadAssetsImages({ variables: { projectId, assetIds: selectedIds } });
    notifySuccess(reportGenerationNotification);
  };

  const isSelected = selectedIds.length > 0;

  const handleBulkRemove = () => {
    setDisabledScroll(true);

    bulkRemoveAssets({
      variables: { ids: selectedIds },
      onSuccess: () => {
        onCompleteBulk();
        setDisabledScroll(false);
      },
      onFailure: errors => {
        notifyError(errors);
        setDisabledScroll(false);
      },
    });
  };

  const handleCreateAsset = () => {
    setDisabledScroll(true);

    createAsset({
      variables: { projectId: projectId, input: { projectId } },
      onSuccess: ({ asset }) => {
        setDisabledScroll(false);
      },
      onFailure: errors => {
        notifyError(errors);
      },
    });
  };

  const isMoreActions =
    moreActions &&
    projectId &&
    (can("features.export_assets") ||
      can("features.export_assets") ||
      can("features.import_assets"));

  const hasExportOptions = can("features.export_assets") && projectId;
  const hasImportOptions = can("features.import_assets") && projectId;

  const [activeSubmenu, setActiveSubmenu] = useState(null);

  return (
    <>
      {isSelected && can("assets.remove") && (
        <>
          <Button size="small" variant="contained" color="primary" onClick={handleBulkRemove}>
            {`Delete asset(s) (${selectedIds.length || totalCount})`}
          </Button>
        </>
      )}
      {isMoreActions && (
        <MenuActions
          id="moreActionsButton"
          style={{ textTransform: "none" }}
          size="small"
          label={<MoreLabel />}
        >
          {can("assets.write") && projectId && (
            <MenuItem data-id="addNewAsset" onClick={handleCreateAsset}>
              <Box mr={1}>
                <NewIcon />
              </Box>
              Add new
            </MenuItem>
          )}

          {/* Export Submenu */}
          {hasExportOptions && (
            <NestedMenuItem
              label="Export"
              icon={<ExportIcon />}
              popupId="export-submenu-legacy"
              activeSubmenu={activeSubmenu}
              setActiveSubmenu={setActiveSubmenu}
            >
              <MenuItem data-id="exportAssetsExcel" onClick={exportExcel}>
                <Box mr={1}>
                  <ExcelIcon fontSize="small" />
                </Box>
                Excel
              </MenuItem>
              <MenuItem data-id="downloadAssetsImages" onClick={downloadImages}>
                <Box mr={1}>
                  <ExcelIcon fontSize="small" />
                </Box>
                Download Images
              </MenuItem>
            </NestedMenuItem>
          )}

          {/* Import Submenu */}
          {hasImportOptions && (
            <NestedMenuItem
              label="Import"
              icon={<ImportIcon />}
              popupId="import-submenu-legacy"
              activeSubmenu={activeSubmenu}
              setActiveSubmenu={setActiveSubmenu}
            >
              <ImportAssetsButton
                keepOpen
                component={MenuItem}
                label={
                  <Box display="flex">
                    <Box mr={1}>
                      <ImportAssetsIcon fontSize="small" />
                    </Box>
                    Import Assets
                  </Box>
                }
                projectId={projectId}
                color="secondary"
                variant="outlined"
                size="small"
              />
              <ImportProcurementSheetButton
                keepOpen
                component={MenuItem}
                label={
                  <Box display="flex">
                    <Box mr={1}>
                      <ImportAssetsIcon fontSize="small" />
                    </Box>
                    Import Procurement Sheet
                  </Box>
                }
                projectId={projectId}
                color="secondary"
                variant="outlined"
                size="small"
                refetchAssets={refetch}
              />
            </NestedMenuItem>
          )}

          {/* Tools Submenu */}
          <NestedMenuItem
            label="Tools"
            icon={<ToolsIcon />}
            popupId="tools-submenu-legacy"
            activeSubmenu={activeSubmenu}
            setActiveSubmenu={setActiveSubmenu}
          >
            <MenuItem data-id="findReplace" onClick={openFindReplace}>
              <Box mr={1}>
                <FindReplaceIcon fontSize="small" />
              </Box>
              Find & Replace
            </MenuItem>
            {canViewShoppingCarts && (
              <MenuItem data-id="projectInCartQuantity" onClick={openProjectInCartModal}>
                <Box mr={1}>
                  <FindReplaceIcon fontSize="small" />
                </Box>
                View Shopping Carts
              </MenuItem>
            )}
          </NestedMenuItem>
        </MenuActions>
      )}
      <SelectProjectAttentionModal
        open={isAttentionOpen}
        onClose={() => setIsAttentionOpen(false)}
      />
      {lookupScanner && (
        <LookupScanner projectId={projectId} clientId={clientId} onClose={toggleLookupScanner} />
      )}
      {isFindReplaceOpen && (
        <FindReplaceWizard
          open={isFindReplaceOpen}
          fields={fields}
        />
      )}
      {isProjectInCartModalOpen && (
        <ProjectInCartQuantityModal
          projectId={projectId}
          onClose={closeProjectInCartModal}
        />
      )}
    </>
  );
};

export default TableBulkActions;
